import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import NotFound from './NotFound';
import { ArrowLeft, Clock, Tag, Calendar, Loader2, BookOpen, ChevronRight } from 'lucide-react';

export default function VidensbankDetail() {
  const { slug } = useParams();
  const { lang, t, getLocalizedValue, localizePath } = useLanguage();

  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Localized labels
  const labels = {
    da: {
      back: 'Tilbage til vidensbanken',
      related: 'Relaterede artikler',
      published: 'Udgivet',
      read_time: 'Læsetid'
    },
    en: {
      back: 'Back to knowledge base',
      related: 'Related articles',
      published: 'Published',
      read_time: 'Read time'
    }
  };

  const currentLabels = labels[lang] || (lang === 'en' ? labels.en : labels.da);

  useEffect(() => {
    const fetchArticleAndRelated = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        // Fetch specific article by slug
        let data = null;
        const targetCmsLang = lang === 'en' ? 'sv' : 'da';
        let res = await fetch(`/_api/public/by-slug/${targetCmsLang}/${slug}`);
        if (res.ok) {
          data = await res.json();
        } else {
          // Fallback if accessed via alternative language or non-localized slug
          const altLang = targetCmsLang === 'da' ? 'sv' : 'da';
          res = await fetch(`/_api/public/by-slug/${altLang}/${slug}`);
          if (res.ok) {
            data = await res.json();
          } else {
            res = await fetch(`/_api/public/by-slug/${slug}`);
            if (res.ok) {
              data = await res.json();
            }
          }
        }

        if (!data || !data.id) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setArticle(data);

        // Fetch related articles (excluding current one)
        let relatedRes = await fetch(`/_api/public/v2/content/artikler?limit=6&sort=updatedAt&order=desc`);
        if (relatedRes.ok) {
          const relatedData = await relatedRes.json();
          const filtered = (relatedData.items || []).filter(item => item.id !== data.id);
          setRelatedArticles(filtered.slice(0, 2)); // Show up to 2 related articles
        }
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchArticleAndRelated();
  }, [slug, lang]);

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Vidensbank | Nordisk Psykiatri' 
    : 'Knowledge base | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Få dybdegående viden, artikler og indsigt i ADHD, autisme, udredning og mental sundhed.'
    : 'Gain in-depth knowledge, articles and insights on ADHD, autism, assessment and mental health.';

  const title = article ? getLocalizedValue(article.titel || article.title) : '';
  const summary = article ? getLocalizedValue(article.resume || article.summary) : '';
  const articleImage = article?.billede?.[0] || article?.image?.[0];

  const articleUrl = `https://www.nordiskpsykiatri.dk${localizePath(`/vidensbank/${slug}`)}`;

  useDocumentMetadata(
    title ? `${title} | Nordisk Psykiatri` : seoTitle,
    summary || seoDesc,
    {
      type: 'article',
      image: articleImage,
      lang,
      canonicalUrl: articleUrl
    }
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-sand-light gap-4">
        <Loader2 className="w-10 h-10 text-petrol-medium animate-spin" />
        <p className="text-sm text-text-dark/60 font-medium">{t('common.loading')}</p>
      </div>
    );
  }

  if (notFound || !article) {
    return <NotFound />;
  }

  const articleTitle = getLocalizedValue(article.titel || article.title);
  const articleCategory = getLocalizedValue(article.kategori || article.category) || (article.terms?.kategori?.[0] ? article.terms.kategori[0].toUpperCase() : 'VIDEN');
  const articleReadTime = getLocalizedValue(article.laesetid || article.read_time);
  const articleAuthor = getLocalizedValue(article.forfatter || article.author);
  const articleContent = getLocalizedValue(article.broedtekst || article.content);
  const articleDate = article.updatedAt 
    ? new Date(article.updatedAt).toLocaleDateString(lang === 'da' ? 'da-DK' : 'en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '';

  const rawImage = article.billede?.[0] || article.image?.[0];
  const imageUrl = rawImage 
    ? (rawImage.includes('assets.auxodev.dk') && !rawImage.includes('/cdn-cgi/image/')
        ? `https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/${rawImage.split('/assets/')[1]}`
        : rawImage)
    : 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/scandinavian-clinic-assessment.jpg';

  return (
    <div className="bg-sand-light min-h-screen pb-24">
      {/* Schema.org Article Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": articleTitle,
            "description": summary,
            "image": imageUrl ? [imageUrl] : undefined,
            "datePublished": article.createdAt || article.updatedAt,
            "dateModified": article.updatedAt,
            "author": {
              "@type": "Person",
              "name": articleAuthor || "Nordisk Psykiatri"
            },
            "publisher": {
              "@type": "MedicalClinic",
              "name": "Nordisk Psykiatri",
              "url": "https://www.nordiskpsykiatri.dk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.nordiskpsykiatri.dk/favicon.svg"
              }
            },
            "inLanguage": lang,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": articleUrl
            },
            "url": articleUrl
          })
        }}
      />

      {/* Navigation Breadcrumb */}
      <div className="mx-auto max-w-4xl px-6 lg:px-8 pt-8">
        <Link
          to={localizePath('/vidensbank')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-text-dark/60 hover:text-petrol-dark transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
          {currentLabels.back}
        </Link>
      </div>

      {/* Article Header */}
      <header className="mx-auto max-w-4xl px-6 lg:px-8 pt-8 pb-12">
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#D1E1BE] text-petrol-dark">
            <Tag className="w-3 h-3" />
            {articleCategory}
          </span>
          {articleReadTime && (
            <span className="inline-flex items-center gap-1.5 text-xs text-text-dark/60 font-medium">
              <Clock className="w-3.5 h-3.5" />
              {articleReadTime}
            </span>
          )}
          {articleAuthor && (
            <span className="inline-flex items-center gap-1.5 text-xs text-text-dark/60 font-medium">
              <BookOpen className="w-3.5 h-3.5" />
              {articleAuthor}
            </span>
          )}
          {articleDate && (
            <span className="inline-flex items-center gap-1.5 text-xs text-text-dark/50">
              <Calendar className="w-3.5 h-3.5" />
              {articleDate}
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-petrol-dark leading-tight mb-6">
          {articleTitle}
        </h1>
      </header>

      {/* Featured Image */}
      <div className="mx-auto max-w-4xl px-6 lg:px-8 mb-16">
        <div className="aspect-[21/9] rounded-3xl overflow-hidden bg-sand-medium/20 shadow-md">
          <img
            src={imageUrl}
            alt={articleTitle}
            className="object-cover w-full h-full"
          />
        </div>
      </div>

      {/* Article Body */}
      <main className="mx-auto max-w-4xl px-6 lg:px-8">
        <div 
          className="prose-article"
          dangerouslySetInnerHTML={{ __html: articleContent }}
        />
      </main>

      {/* Related Articles Section */}
      {relatedArticles.length > 0 && (
        <section className="mx-auto max-w-4xl px-6 lg:px-8 mt-24 pt-16 border-t border-sand-medium/60">
          <h2 className="font-display text-2xl font-bold text-petrol-dark mb-8">
            {currentLabels.related}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {relatedArticles.map((item) => {
              const rTitle = getLocalizedValue(item.titel || item.title);
              const rSummary = getLocalizedValue(item.resume || item.summary);
              const rCategory = getLocalizedValue(item.kategori || item.category) || (item.terms?.kategori?.[0] ? item.terms.kategori[0].toUpperCase() : 'VIDEN');
              const rawRImage = item.billede?.[0] || item.image?.[0];
              const rImageUrl = rawRImage
                ? (rawRImage.includes('assets.auxodev.dk') && !rawRImage.includes('/cdn-cgi/image/')
                    ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/${rawRImage.split('/assets/')[1]}`
                    : rawRImage)
                : 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/scandinavian-clinic-assessment.jpg';
              const rSlug = typeof item.slug === 'object' ? getLocalizedValue(item.slug) : (item.slug || item.id);

              return (
                <Link
                  key={item.id}
                  to={localizePath(`/vidensbank/${rSlug}`)}
                  className="flex flex-col bg-sand-light rounded-2xl border border-sand-medium/40 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-sand-medium/20 relative">
                    <img
                      src={rImageUrl}
                      alt={rTitle}
                      className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#D1E1BE] text-petrol-dark backdrop-blur-sm shadow-sm">
                        {rCategory}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="font-display text-lg font-bold text-petrol-dark mb-2 group-hover:text-petrol-medium transition-colors line-clamp-2">
                        {rTitle}
                      </h3>
                      <p className="text-xs text-text-dark/70 line-clamp-2 mb-4">
                        {rSummary}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-petrol-dark group-hover:text-petrol-medium transition-colors">
                      {lang === 'da' ? 'Læs mere' : 'Read more'}
                      <ChevronRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
